import { inject, Injectable } from "@angular/core";
import * as L from "leaflet";
import "leaflet.markercluster";
import { takeUntil } from "rxjs";
import { CommercialClusterPopupComponent } from "src/app/map/map-marker/commercial/commercial-cluster-popup.component";
import { IMapPointClick } from "@models/map/map.model";
import { BaseMapComponentsService, IMarkerClusterParams } from "../MapComponentsBaseService";
import { ICommercialSale, ICommercialSales } from "src/models/commercial/ICommercialSale";
import { CommercialClusterMapMarkerComponent } from "src/app/map/map-marker/commercial/commercial-cluster-map-marker.component";
import { CommercialMapMarkerComponent } from "src/app/map/map-marker/commercial/commercial-map-marker.component";
import { MapMode, MapStateService } from "../map/map-state.service";

//Todo: se om noe av cluster-koden skal flyttes til en generell service eller hjelpeklasse
@Injectable()
export class CommercialMapComponentsService extends BaseMapComponentsService {
    constructor() {
        super();
        this.mapActionsService.onMapClicked$.pipe(takeUntil(this.destroy$)).subscribe((click) => this.onCommercialMapClicked(click));
    }

    private onCommercialMapClicked(click: IMapPointClick): void {
        if (click.zoom >= this.mapStateService.zoomLimitClickAny && this.mapStateService.mapModeSignal() === MapMode.CommercialSale) {
            this.mapDataService.getNearestUnits(click.lat, click.lng).subscribe((units) => {
                if (units.length > 0) {
                    this.openPopupForCluster(click, units);
                }
            });
        }
    }

    updateLayers(sales: ICommercialSales) {
        if (this.layer) {
            this.layer.clearLayers();
        }

        if (sales == null) return;

        if (this.map) {
            this.layer = L.layerGroup().addTo(this.map);
            if (sales.simplifiedSearch) {
                this.createOverviewMarkers(sales.data);
            } else {
                this.createSaleMarkers(sales.data);
            }
        }
    }
    private createCommercialMarkerElement(sale: ICommercialSale): HTMLElement {
        const [element, component] = this.createElementWithComponent(CommercialMapMarkerComponent);
        component.setInput("data", sale);
        return element;
    }
    private createCommercialMarker(sale: ICommercialSale): L.Marker {
        const htmlElement = this.createCommercialMarkerElement(sale);
        const className = "commercial-marker";
        const lat = sale.latLng?.lat;
        const lng = sale.latLng?.lng;
        const originalObject = sale;
        return this.createMarker(lat, lng, htmlElement, className, originalObject);
    }
    private createOverviewMarker(sale: ICommercialSale) {
        const icon = L.divIcon({
            html: "<div></div>",
            className: "overview-marker",
            iconSize: [30, 30],
        });
        const marker = L.marker([sale.latLng?.lat, sale.latLng?.lng], { icon: icon });
        (marker as any).originalObject = sale;
        return marker;
    }

    private createSaleMarkers(estates: ICommercialSale[]): void {
        const estateMarkers: any[] = [];

        estates.forEach((sale) => {
            if (sale.latLng?.lat != null && sale.latLng?.lng != null) {
                const marker: L.Marker = this.createCommercialMarker(sale);
                const popup: L.Popup = this.createCommercialMarkerPopup(sale);

                marker.bindPopup(popup).openPopup();

                estateMarkers.push(marker);
            }
        });

        const markerClusterParams: IMarkerClusterParams = {
            className: "map-cluster-wrapper",
            iconSize: [116, 22],
            markers: estateMarkers,
            targetEventListener: (e, t) => {},
            targetEvents: ["clusterclick", "clustermouseover", "clustermouseout"],
            chunkedLoading: true,
            maxClusterRadius: 110,
            spiderfyOnMaxZoom: false,
            zoomToBoundsOnClick: false,
        };
        this.createMarkerCluster(this.createMarkerClusterGroup(markerClusterParams), markerClusterParams);
    }

    private createOverviewMarkers(sales: ICommercialSale[]): void {
        const estateMarkers: any[] = [];

        sales.forEach((item) => {
            if (item.latLng?.lat != null && item.latLng?.lng != null) {
                const marker: any = this.createOverviewMarker(item);
                estateMarkers.push(marker);
            }
        });

        this.createOverviewClusterGroup({
            className: "overview-marker",
            iconSize: [30, 30],
            markers: estateMarkers,
            targetEventListener: (e, t) => {},
            targetEvents: ["clusterclick", "clustermouseover", "clustermouseout"],
            chunkedLoading: true,
            maxClusterRadius: 110,
            spiderfyOnMaxZoom: false,
            zoomToBoundsOnClick: true,
        });
    }
    public createCommercialClusterElement(sales: ICommercialSale[]): HTMLElement {
        const [element, component] = this.createElementWithComponent(CommercialClusterMapMarkerComponent);
        component.setInput("data", sales);
        return element;
    }

    private createMarkerClusterGroup(markerClusterParams: IMarkerClusterParams): L.MarkerClusterGroup {
        let sales: ICommercialSale[] = [];
        let leaflet = this.getMarkerClusterGroup();

        const markerCluster = leaflet.markerClusterGroup({
            iconCreateFunction: (cluster: any) => {
                const markers = cluster.getAllChildMarkers();
                markers.forEach((m: any) => {
                    sales.push((m as any).originalObject);
                });
                const html = this.createCommercialClusterElement(sales);
                sales = [];
                return (L as any).divIcon({
                    iconSize: markerClusterParams.iconSize,
                    html: html,
                    className: markerClusterParams.className,
                    iconAnchor: [15, 15],
                });
            },
            showCoverageOnHover: true,
            polygonOptions: {
                color: "#3d91c6",
                weight: 2,
            },
            spiderfyOnMaxZoom: markerClusterParams.spiderfyOnMaxZoom,
            chunkedLoading: markerClusterParams.chunkedLoading,
            zoomToBoundsOnClick: markerClusterParams.zoomToBoundsOnClick,
            maxClusterRadius: () => {
                return markerClusterParams.maxClusterRadius;
            },
        });
        return markerCluster;
    }
    protected override openPopupForCluster(position: { lat: number; lng: number }, sales: any[]): void {
        const [htmlElement, component] = this.createElementWithComponent(CommercialClusterPopupComponent);
        component.setInput("data", sales);
        this.openPopupWithSales(htmlElement, sales?.length, position);
    }
    private createCommercialMarkerPopup(sale: ICommercialSale): L.Popup {
        const [htmlElement, component] = this.createElementWithComponent(CommercialClusterPopupComponent);

        component.setInput("data", [sale]);

        return this.createMarkerPopup(htmlElement, "cluster-popup");
    }
}
